import type { Field } from '@evidia/components';
import { SimpleForm } from '@evidia/components';
import type { TeachingLessonRecordType } from '@evidia/shared';
import { StudentDefaultTeacherType } from '@evidia/shared';
import { groupBy } from 'lodash-es';
import { useActiveSchoolId } from '../../../../hooks/useActiveSchoolId';
import { useSocketMessage } from '../../../../hooks/useSocketMessage';
import { trpc } from '../../../../utils/trcp';
import DefaultTeacher from '../../../DefaultTeacher';
import { EvidenceHeaderContainer, TabTitle } from '../styles';
import { SectionHeader } from './SectionHeader';

const GROUPES_ORDER = ['TZBJ', 'PPV', 'OUV', 'ZP', 'OP', 'PV-UV', 'PV-ZP'];

export type Group = {
	key: string;
	title: TeachingLessonRecordType;
	items: { date?: Date; userId?: string; id: string }[];
};

interface Props {
	studentId: string;
}

const TeachingTab: React.FC<Props> = ({ studentId }) => {
	const { activeSchoolId } = useActiveSchoolId();

	const getTeachingLessonRecords = trpc.teachingLessonRecord.getTeachingLessonRecords.useQuery({
		schoolId: activeSchoolId,
		studentId,
	});

	const updateTeachingLessonRecord = trpc.teachingLessonRecord.updateTeachingLessonRecord.useMutation();

	const getSchoolTeachers = trpc.school.getSchoolTeachers.useQuery({ schoolId: activeSchoolId });
	const getDefaultTeacher = trpc.studentDefaultTeacher.getDefaultTeacher.useQuery({
		schoolId: activeSchoolId,
		studentId,
		type: StudentDefaultTeacherType.TEACHING,
	});

	useSocketMessage('TeachingLessonRecordMessage', d => {
		if (d.studentId === studentId) {
			getTeachingLessonRecords.refetch();
		}
	});

	const FIELDS: Field[] = [];

	const teachers =
		getSchoolTeachers.data?.map(teacher => ({
			key: teacher.id,
			value: `${teacher.firstName} ${teacher.lastName}`,
		})) ?? [];

	const gridPositions: string[][] = [];
	const styleMap: Record<string, React.CSSProperties> = {};

	const gruped = groupBy(getTeachingLessonRecords.data, 'type');

	const preparedGroups = Object.keys(gruped)
		.map(key => ({
			key,
			title: key,
			items: gruped[key].sort((a, b) => a.id.localeCompare(b.id)),
		}))
		.sort((a, b) => GROUPES_ORDER.indexOf(a.key) - GROUPES_ORDER.indexOf(b.key));

	const initialValues: Record<string, Date | string | undefined | null> = {};

	let iterator = 0;
	preparedGroups.forEach(g => {
		const groupKey = `group-${g.key}`;

		FIELDS.push({
			key: groupKey,
			type: 'custom',
			render: () => <SectionHeader section={g as any} />,
			label: '',
			required: false,
		});

		gridPositions.push([groupKey, groupKey]);

		g.items.forEach((item, i) => {
			FIELDS.push({
				key: `calendar-${iterator}#${item.id}`,
				type: 'day-picker',
				required: true,
				// required: true,
				// label: 'Konec jízdy',
				label: `${i + 1}. lekce`,
				// fields: [],
			});

			initialValues[`calendar-${iterator}#${item.id}`] = item.date;

			FIELDS.push({
				key: `teacher-${iterator}#${item.id}`,
				required: true,
				label: ' ',
				type: 'select',
				placeholder: 'Vyberte učitele',
				loading: getSchoolTeachers.isLoading,
				values: [{ key: '__empty', value: 'Nevybráno' }, ...teachers],
			});
			initialValues[`teacher-${iterator}#${item.id}`] = item.userId ?? '__empty';

			FIELDS.push({
				key: `topic-${iterator}#${item.id}`,
				type: 'string',
				required: false,
				placeholder: `Probírané téma`,
			});

			initialValues[`topic-${iterator}#${item.id}`] = item.topic;

			gridPositions.push([`calendar-${iterator}`, `teacher-${iterator}`]);
			gridPositions.push([`topic-${iterator}`, `topic-${iterator}`]);

			// ALERT!!! For grid psitions to work correctly, hash part needs to be removed at grid styles generation.
			// For reference see packages/components/src/components/SimpleForm/utils.ts#77 and SimpleForm.tsx#L396

			iterator++;
		});
	});

	const onFinish = async (values: Record<string, any>) => {
		const records: { id: string; userId?: string; date?: Date; topic?: string }[] = [];
		const recordMap: Record<string, { userId?: string; date?: Date; topic?: string }> = {};

		const recordValues = Object.keys(values);
		recordValues.forEach(key => {
			const id = key.split('#')[1];
			if (!recordMap[id]) {
				recordMap[id] = {};
			}

			if (key.includes('teacher')) {
				const teacher = values[key] === '__empty' ? undefined : values[key];
				recordMap[id] = { ...recordMap[id], userId: teacher };
			} else if (key.includes('calendar')) {
				const date = values[key];
				recordMap[id] = { ...recordMap[id], date };
			} else if (key.includes('topic')) {
				const topic = values[key];
				recordMap[id] = { ...recordMap[id], topic };
			}
		});

		Object.keys(recordMap).forEach(recordId => {
			const record = recordMap[recordId];
			const originalRecord = getTeachingLessonRecords.data?.find(r => r.id === recordId);
			if (
				record.userId !== originalRecord?.userId ||
				record.date !== originalRecord?.date ||
				record.topic !== originalRecord?.topic
			) {
				records.push({ id: recordId, userId: record.userId, date: record.date, topic: record.topic });
			}
		});

		await updateTeachingLessonRecord.mutateAsync({
			schoolId: activeSchoolId,
			studentId,
			data: records.map(r => ({
				id: r.id,
				userId: r.userId ?? null,
				date: r.date ?? null,
				topic: r.topic ?? null,
			})),
		});
		await getTeachingLessonRecords.refetch();
	};

	return (
		<>
			<TabTitle>Evidence výuky</TabTitle>
			<EvidenceHeaderContainer>
				<DefaultTeacher
					studentId={studentId}
					type={StudentDefaultTeacherType.TEACHING}
					defaultTeacher={getDefaultTeacher.data}
					refetch={getDefaultTeacher.refetch as unknown as () => Promise<void>}
				/>
			</EvidenceHeaderContainer>

			<SimpleForm
				fields={FIELDS}
				onFinish={onFinish}
				submitButtonTitle="Uložit"
				lessSpaceBetweenItems
				fieldsGridMap={gridPositions}
				style={styleMap}
				initialValues={initialValues}
			/>
		</>
	);
};

export default TeachingTab;
